import { useRef, useState } from "react";
import { setEta, submit } from "../api/webcheckinApi";
import { runSubmitOnce } from "../lib/submitFlow";
import { useWizardContext } from "./shared";
import type { Step } from "../state/wizard";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TimePicker } from "@/components/ui/time-picker";

const KNOWN_STEPS: readonly Step[] = [
  "welcome", "register", "companions", "consent", "eta",
  "submitting", "success", "arrival",
];
function isKnownStep(s: string | null): s is Step {
  return !!s && (KNOWN_STEPS as readonly string[]).includes(s);
}

export function EtaSubmitScreen({ token }: { token: string }) {
  const { state, dispatch } = useWizardContext();
  const session = state.payload!.session;
  const checkInTime = state.payload!.property.checkInTime;
  // Preserve user-typed values across visits, never seed calculated defaults.
  const [etaTime, setEtaTime] = useState<string | null>(session.etaTime ?? null);
  const [specialRequests, setSpecialRequests] = useState<string>(session.specialRequests ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set synchronously on the first click: a second click before React has
  // re-rendered the disabled button is ignored (lib/submitFlow.ts).
  const inFlight = useRef(false);

  async function handleSubmit() {
    await runSubmitOnce(inFlight, {
      begin: () => { setBusy(true); setError(null); },
      saveEta: () => setEta(token, etaTime || null, specialRequests.trim() || null),
      submitting: () => dispatch({ type: "goto", step: "submitting" }),
      submit: () => submit(token),
      // Also when the server says the check-in was already submitted (a
      // second tab, a retried click): the guest is done — never back to ETA.
      succeeded: (out) => {
        if (out) {
          try { sessionStorage.setItem(`success:${token}`, JSON.stringify(out)); } catch { /* storage disabled */ }
        }
        dispatch({ type: "goto", step: "success" });
      },
      // A 412 with a `step` hint (missing field on Register / Companions /
      // Consent): navigate the guest THERE instead of stranding them on ETA.
      goToStep: (step, message) => { setError(message); dispatch({ type: "goto", step: step as Step }); },
      failed: (message) => { setError(message); dispatch({ type: "goto", step: "eta" }); },
      isKnownStep,
      end: () => setBusy(false),
    });
  }

  return (
    <div>
      <h1 className="screen-h1">Almost done — arrival details</h1>
      <p className="screen-p mt-2">Optional. Helps us prepare the room and have a key ready when you arrive.</p>

      <div className="mt-5 space-y-5">
        <div className="space-y-1.5">
          <Label>Expected arrival time (24h)</Label>
          <TimePicker value={etaTime} onChange={setEtaTime} minuteStep={15} />
          {/* The hotel's own hour (pms_workspace_settings.check_in_time). A hotel with
              none on record shows no line rather than an hour nobody set. */}
          {checkInTime && <p className="text-xs text-muted-foreground">Standard check-in opens at {checkInTime}.</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="reqs">Additional requests</Label>
          <Textarea
            id="reqs"
            rows={4}
            maxLength={1000}
            value={specialRequests}
            onChange={(e) => setSpecialRequests(e.target.value)}
            placeholder="Dietary restrictions, accessibility needs, occasions to mark…"
          />
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <Button className="mt-6 w-full h-11" disabled={busy} onClick={handleSubmit}>
        {busy ? "Submitting…" : "Submit check-in"}
      </Button>
    </div>
  );
}
